import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save,
  Plus,
  Trash2,
  Lock,
  Unlock,
  Edit2,
  X,
  Check,
  Eye,
  ExternalLink,
  Image,
  FileText,
  ChevronLeft,
  Heart,
  Camera
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import FileUpload from '../../components/FileUpload';
import CloudinaryUpload from '../../components/ui/CloudinaryUpload';
import MarkdownContent from '../../components/ui/MarkdownContent';
import ImagePositioner from '../../components/ui/ImagePositioner';
import SortableList from '../../components/SortableList';
import AdminPreliminarySpheres from '../../components/AdminPreliminarySpheres';

const CONTENT_TYPES = [
  { value: 'text', label: 'Texto', icon: FileText },
  { value: 'video', label: 'Video' },
  { value: 'pdf', label: 'PDF' },
  { value: 'link', label: 'Enlace' }
];

const BLOCK_COLORS = [
  '#E8DFD0', '#C9B896', '#D4A84B', '#E8735A', 
  '#4FB5A9', '#7B8EBF', '#D4CCC4', '#A8B8A0'
];

// Helper to determine if a color is light
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

const AdminStudentArea = () => {
  const { slug: routeSlug } = useParams();
  const [searchParams] = useSearchParams();
  // Get slug from route params, query params, or default
  const courseSlug = routeSlug || searchParams.get('slug') || 'facilitadoras-sosten-energetico';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHeroEditor, setShowHeroEditor] = useState(false);
  const [data, setData] = useState({
    hero_image: '',
    welcome_title: '',
    welcome_text: '',
    blocks: []
  });
  const isLimitesTpl = courseSlug === 'limites' || data?.student_template === 'limites';
  
  // Navigation state
  const [selectedModule, setSelectedModule] = useState(null);
  
  // Edit modals
  const [editingBlock, setEditingBlock] = useState(null);
  const [editingSubBlock, setEditingSubBlock] = useState(null);
  const [editingWelcome, setEditingWelcome] = useState(false);

  useEffect(() => {
    fetchData();
  }, [courseSlug]);

  const fetchData = async () => {
    try {
      const response = await api.get(`/admin/special-course/${courseSlug}/student-area`);
      setData(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Refetch latest preliminary_blocks just before saving, so we never overwrite
      // them with stale state if the admin added/edited spheres in the meantime.
      let payload = { ...(data || {}) };
      try {
        const fresh = await api.get(`/admin/special-courses/${courseSlug}/preliminary-modules`);
        payload.preliminary_blocks = Array.isArray(fresh.data) ? fresh.data : (data?.preliminary_blocks || []);
      } catch {
        // If the endpoint doesn't exist yet on this backend, fall back to whatever we have.
        payload.preliminary_blocks = data?.preliminary_blocks || [];
      }
      await api.put(`/admin/special-course/${courseSlug}/student-area`, payload);
      toast.success('Cambios guardados correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    try {
      const response = await api.post(`/admin/special-course/${courseSlug}/student-area/block`, {
        title: 'Nuevo Módulo',
        subtitle: '',
        color: BLOCK_COLORS[data.blocks.length % BLOCK_COLORS.length],
        order: data.blocks.length,
        is_locked: true
      });
      
      // Update local state
      setData({
        ...data,
        blocks: [...data.blocks, response.data.block]
      });
      
      toast.success('Módulo añadido');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al añadir módulo');
    }
  };

  const deleteBlock = async (blockId) => {
    if (!window.confirm('¿Eliminar este módulo y todo su contenido?')) return;
    
    try {
      await api.delete(`/admin/special-course/${courseSlug}/student-area/block/${blockId}`);
      
      setData({
        ...data,
        blocks: data.blocks.filter(b => b.id !== blockId)
      });
      
      toast.success('Módulo eliminado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar módulo');
    }
  };

  const addSubBlock = async (blockId, moduleIndex = 1) => {
    try {
      const response = await api.post(`/admin/special-course/${courseSlug}/student-area/block/${blockId}/sub-block`, {
        title: 'Nueva página',
        content_type: 'text',
        order: (selectedModule?.sub_blocks?.length || 0),
        module_index: moduleIndex,
      });
      // Update local state
      const updatedBlocks = data.blocks.map(b => {
        if (b.id === blockId) {
          return {
            ...b,
            sub_blocks: [...(b.sub_blocks || []), response.data.sub_block]
          };
        }
        return b;
      });
      
      setData({ ...data, blocks: updatedBlocks });
      
      // Update selected module
      if (selectedModule?.id === blockId) {
        setSelectedModule({
          ...selectedModule,
          sub_blocks: [...(selectedModule.sub_blocks || []), response.data.sub_block]
        });
      }
      
      toast.success('Página añadida');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al añadir página');
    }
  };

  const deleteSubBlock = async (blockId, subBlockId) => {
    if (!window.confirm('¿Eliminar esta página?')) return;
    try {
      await api.delete(`/admin/special-course/${courseSlug}/student-area/block/${blockId}/sub-block/${subBlockId}`);
      
      const updatedBlocks = data.blocks.map(b => {
        if (b.id === blockId) {
          return {
            ...b,
            sub_blocks: (b.sub_blocks || []).filter(s => s.id !== subBlockId)
          };
        }
        return b;
      });
      
      setData({ ...data, blocks: updatedBlocks });
      
      if (selectedModule?.id === blockId) {
        setSelectedModule({
          ...selectedModule,
          sub_blocks: (selectedModule.sub_blocks || []).filter(s => s.id !== subBlockId)
        });
      }
      
      toast.success('Página eliminada');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const updateBlock = async (blockId, updates) => {
    try {
      await api.put(`/admin/special-course/${courseSlug}/student-area/block/${blockId}`, updates);
      
      const updatedBlocks = data.blocks.map(b => 
        b.id === blockId ? { ...b, ...updates } : b
      );
      
      setData({ ...data, blocks: updatedBlocks });
      setEditingBlock(null);
      toast.success('Módulo actualizado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar');
    }
  };

  const updateSubBlock = async (blockId, subBlockId, updates) => {
    try {
      await api.put(`/admin/special-course/${courseSlug}/student-area/block/${blockId}/sub-block/${subBlockId}`, updates);
      
      const updatedBlocks = data.blocks.map(b => {
        if (b.id === blockId) {
          return {
            ...b,
            sub_blocks: (b.sub_blocks || []).map(s => 
              s.id === subBlockId ? { ...s, ...updates } : s
            )
          };
        }
        return b;
      });
      
      setData({ ...data, blocks: updatedBlocks });
      
      if (selectedModule?.id === blockId) {
        setSelectedModule({
          ...selectedModule,
          sub_blocks: (selectedModule.sub_blocks || []).map(s => 
            s.id === subBlockId ? { ...s, ...updates } : s
          )
        });
      }
      
      setEditingSubBlock(null);
      toast.success('Página actualizada');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar');
    }
  };

  // Reorder modules (blocks) — optimistic UI + bulk save
  const reorderBlocks = async (newBlocks) => {
    const previous = data.blocks;
    const reordered = newBlocks.map((b, i) => ({ ...b, order: i }));
    setData({ ...data, blocks: reordered });
    try {
      await api.put(
        `/admin/special-course/${courseSlug}/student-area/blocks/reorder`,
        { ids: reordered.map(b => b.id) }
      );
      toast.success('Orden de módulos actualizado');
    } catch {
      setData({ ...data, blocks: previous });
      toast.error('No se pudo guardar el nuevo orden');
    }
  };

  // Reorder sub-blocks (lessons) inside a module — optimistic UI + bulk save
  const reorderSubBlocks = async (blockId, newSubs) => {
    const block = data.blocks.find(b => b.id === blockId);
    if (!block) return;
    const previousSubs = block.sub_blocks || [];
    const reordered = newSubs.map((s, i) => ({ ...s, order: i }));

    const updatedBlocks = data.blocks.map(b =>
      b.id === blockId ? { ...b, sub_blocks: reordered } : b
    );
    setData({ ...data, blocks: updatedBlocks });
    if (selectedModule?.id === blockId) {
      setSelectedModule({ ...selectedModule, sub_blocks: reordered });
    }
    try {
      await api.put(
        `/admin/special-course/${courseSlug}/student-area/block/${blockId}/sub-blocks/reorder`,
        { ids: reordered.map(s => s.id) }
      );
      toast.success('Orden actualizado');
    } catch {
      // revert
      const revertBlocks = data.blocks.map(b =>
        b.id === blockId ? { ...b, sub_blocks: previousSubs } : b
      );
      setData({ ...data, blocks: revertBlocks });
      if (selectedModule?.id === blockId) {
        setSelectedModule({ ...selectedModule, sub_blocks: previousSubs });
      }
      toast.error('No se pudo guardar el nuevo orden');
    }
  };

  // Get module display info
  const getModuleDisplay = (block) => {
    const title = block.title || '';
    const color = block.color || '#E8DFD0';
    const textColor = isLightColor(color) ? '#6B5B3D' : '#FFFFFF';
    
    let displayNumber = '';
    let phaseWord = '';
    
    if (title.toLowerCase().includes('índice')) {
      displayNumber = 'i';
    } else if (title.toLowerCase().includes('glosario')) {
      displayNumber = 'G';
    } else {
      const match = title.match(/módulo\s*(\d+)/i);
      if (match) {
        displayNumber = match[1];
        const num = parseInt(match[1]);
        if (num >= 1 && num <= 3) phaseWord = 'Habitar';
        else if (num >= 4 && num <= 6) phaseWord = 'Estructurar';
        else if (num >= 7 && num <= 9) phaseWord = 'Trascender';
        else if (num >= 10) phaseWord = 'Sostén energético';
      }
    }
    
    return { displayNumber, phaseWord, color, textColor };
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // VIEW: Inside a module - showing lessons gallery
  if (selectedModule) {
    const moduleDisplay = getModuleDisplay(selectedModule);
    const subBlocks = selectedModule.sub_blocks || [];

    return (
      <div className="pt-20 min-h-screen bg-[#FDFCFA]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setSelectedModule(null)}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver al programa
            </button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditingBlock(selectedModule)}
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Editar módulo
              </Button>
              <Button onClick={saveAll} disabled={saving} className="btn-sage" size="sm">
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>

          {/* Module header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {selectedModule.title}
            </h1>
            <p className="text-gray-500 flex items-center gap-2">
              <Heart className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              {selectedModule.subtitle}
            </p>
          </div>

          {/* Lessons gallery */}
          {isLimitesTpl ? (() => {
            const m1Title = selectedModule.module_1_title || 'Módulo 1';
            const m2Title = selectedModule.module_2_title || 'Módulo 2';
            const group = (idx) => [...subBlocks]
              .filter(s => (Number(s.module_index) === 2 ? 2 : 1) === idx)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            const renderCard = (lesson, index, { dragHandleProps, isDragging }) => (
              <div
                className={`group bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-all ${isDragging ? 'shadow-2xl ring-2 ring-[#c9a227]/30' : ''}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                  {lesson.image_url ? (
                    <img
                      src={lesson.image_url}
                      alt={lesson.title}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: lesson.image_position || 'center center' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <Image className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <button
                    type="button"
                    {...dragHandleProps}
                    className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white/90 backdrop-blur-sm rounded shadow text-gray-600 cursor-grab active:cursor-grabbing touch-none"
                    aria-label="Arrastrar para reordenar"
                    data-testid={`drag-lesson-${lesson.id}`}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.2"/><circle cx="11" cy="3" r="1.2"/><circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/><circle cx="5" cy="13" r="1.2"/><circle cx="11" cy="13" r="1.2"/></svg>
                  </button>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={() => setEditingSubBlock({ ...lesson, blockId: selectedModule.id })}
                      className="p-1.5 bg-white rounded shadow hover:bg-gray-50"
                      data-testid={`edit-lesson-${lesson.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => deleteSubBlock(selectedModule.id, lesson.id)}
                      className="p-1.5 bg-white rounded shadow hover:bg-red-50"
                      data-testid={`delete-lesson-${lesson.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="p-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {index + 1}. {lesson.title}
                  </span>
                </div>
              </div>
            );
            return (
              <>
                {[1, 2].map((idx) => {
                  const items = group(idx);
                  const sectionTitle = idx === 1 ? m1Title : m2Title;
                  return (
                    <div key={idx} className="mb-10" data-testid={`admin-module-section-${idx}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-[#8a7428] bg-[#FDFBF5] border border-[#D4A84B]/30 px-2.5 py-0.5 rounded-full">
                          {idx === 1 ? '1ª sección' : '2ª sección'}
                        </span>
                        <h3 className="font-heading text-lg text-[hsl(var(--text-primary))]">{sectionTitle}</h3>
                      </div>
                      {items.length > 0 ? (
                        <SortableList
                          layout="grid"
                          items={items}
                          onReorder={(newSubs) => {
                            // Reorder only within this group, preserving the other group's items
                            const otherGroup = [...subBlocks].filter(s => (Number(s.module_index) === 2 ? 2 : 1) !== idx);
                            reorderSubBlocks(selectedModule.id, [...otherGroup, ...newSubs]);
                          }}
                          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
                          renderItem={renderCard}
                        />
                      ) : (
                        <p className="text-sm text-gray-400 italic mb-4">Aún no hay páginas en esta sección.</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          onClick={() => addSubBlock(selectedModule.id, idx)}
                          className="aspect-[16/10] border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-gray-300 hover:text-gray-500 transition-colors"
                          data-testid={`add-page-section-${idx}`}
                        >
                          <Plus className="w-8 h-8 mb-2" />
                          <span className="text-sm">Nueva página en {sectionTitle}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })() : (
          <>
          {/* Lessons gallery - 2 columns sortable like Notion */}
          <SortableList
            layout="grid"
            items={[...subBlocks].sort((a, b) => (a.order || 0) - (b.order || 0))}
            onReorder={(newSubs) => reorderSubBlocks(selectedModule.id, newSubs)}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
            renderItem={(lesson, index, { dragHandleProps, isDragging }) => (
              <div
                className={`group bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-all ${isDragging ? 'shadow-2xl ring-2 ring-[#c9a227]/30' : ''}`}
              >
                {/* Lesson image */}
                <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                  {lesson.image_url ? (
                    <img
                      src={lesson.image_url}
                      alt={lesson.title}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: lesson.image_position || 'center center' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <Image className="w-12 h-12 text-gray-300" />
                    </div>
                  )}

                  {/* Drag handle (top-left, hover) */}
                  <button
                    type="button"
                    {...dragHandleProps}
                    className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white/90 backdrop-blur-sm rounded shadow text-gray-600 cursor-grab active:cursor-grabbing touch-none"
                    aria-label="Arrastrar para reordenar"
                    data-testid={`drag-lesson-${lesson.id}`}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.2"/><circle cx="11" cy="3" r="1.2"/><circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/><circle cx="5" cy="13" r="1.2"/><circle cx="11" cy="13" r="1.2"/></svg>
                  </button>

                  {/* Edit/Delete buttons on hover */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={() => setEditingSubBlock({ ...lesson, blockId: selectedModule.id })}
                      className="p-1.5 bg-white rounded shadow hover:bg-gray-50"
                      data-testid={`edit-lesson-${lesson.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => deleteSubBlock(selectedModule.id, lesson.id)}
                      className="p-1.5 bg-white rounded shadow hover:bg-red-50"
                      data-testid={`delete-lesson-${lesson.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Lesson title */}
                <div className="p-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {index + 1}. {lesson.title}
                  </span>
                </div>
              </div>
            )}
          />

          {/* Add new page button (outside sortable list, fixed at the end) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => addSubBlock(selectedModule.id)}
              className="aspect-[16/10] border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-gray-300 hover:text-gray-500 transition-colors"
            >
              <Plus className="w-8 h-8 mb-2" />
              <span className="text-sm">Nueva página</span>
            </button>
          </div>
          </>
          )}
        </div>

        {/* Edit Sub-block Modal */}
        {editingSubBlock && (
          <EditSubBlockModal
            subBlock={editingSubBlock}
            courseSlug={courseSlug}
            limitesTpl={isLimitesTpl}
            block={data?.blocks?.find(b => b.id === editingSubBlock.blockId)}
            onSave={(updates) => updateSubBlock(editingSubBlock.blockId, editingSubBlock.id, updates)}
            onClose={() => setEditingSubBlock(null)}
          />
        )}

        {/* Edit Block Modal */}
        {editingBlock && (
          <EditBlockModal
            block={editingBlock}
            courseSlug={courseSlug}
            limitesTpl={isLimitesTpl}
            onSave={(updates) => updateBlock(editingBlock.id, updates)}
            onClose={() => setEditingBlock(null)}
          />
        )}
      </div>
    );
  }

  // VIEW: Main - showing modules gallery (like student view but editable)
  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA]">
      {/* Hero Section */}
      <div className="relative h-[250px] lg:h-[300px] overflow-hidden group">
        <img 
          src={data.hero_image || 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?fit=crop&w=1200&q=80'}
          alt="Hero"
          className="w-full h-full object-cover"
          style={{ objectPosition: data.hero_image_position || 'center center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FDFCFA] via-[#FDFCFA]/30 to-transparent" />
        
        {/* Edit Hero Image Button */}
        <button
          onClick={() => setShowHeroEditor(true)}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                     opacity-0 group-hover:opacity-100 transition-opacity
                     bg-black/50 hover:bg-black/70 text-white px-4 py-2 rounded-full
                     flex items-center gap-2 text-sm"
        >
          <Camera className="w-4 h-4" />
          Cambiar imagen de portada
        </button>
        
        {/* Back button */}
        <Link 
          to="/admin/formaciones"
          className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/80 hover:text-white bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Link>
        
        {/* Action buttons */}
        <div className="absolute top-6 right-6 flex gap-2">
          <a 
            href={`/formaciones/${courseSlug}/curso`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/90 text-gray-700 hover:bg-white text-sm"
          >
            <Eye className="w-4 h-4" />
            Vista Previa
            <ExternalLink className="w-3 h-3" />
          </a>
          <Button onClick={saveAll} disabled={saving} className="btn-sage rounded-full" size="sm">
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Guardando...' : 'Guardar Todo'}
          </Button>
        </div>
      </div>

      {/* Hero Image Editor Modal */}
      {showHeroEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading text-gray-800">Cambiar imagen de portada</h3>
              <button 
                onClick={() => setShowHeroEditor(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <CloudinaryUpload
              value={data.hero_image}
              onChange={(url) => {
                setData({ ...data, hero_image: url });
                toast.success('Imagen actualizada. Ajusta el encuadre y guarda los cambios.');
              }}
              folder="escuela/cursos"
              aspectRatio="video"
              placeholder="Sube una imagen de portada (recomendado 1200x400)"
            />

            <p className="text-xs text-gray-500 mt-4 text-center">
              O introduce una URL externa:
            </p>
            <Input
              value={data.hero_image || ''}
              onChange={(e) => setData({ ...data, hero_image: e.target.value })}
              placeholder="https://..."
              className="mt-2"
            />

            {/* Position picker: arrastrar el punto para ajustar el encuadre */}
            {data.hero_image && (
              <div className="mt-5">
                <p className="text-xs text-gray-600 mb-2 font-medium">
                  Ajuste de encuadre <span className="text-gray-400 font-normal">(arrastra el punto para mover la imagen dentro del marco)</span>
                </p>
                <ImagePositioner
                  imageUrl={data.hero_image}
                  position={data.hero_image_position || 'center center'}
                  onChange={(pos) => setData({ ...data, hero_image_position: pos })}
                  aspectRatio="3/1"
                />
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowHeroEditor(false)}>
                Cancelar
              </Button>
              <Button 
                className="btn-gold"
                onClick={() => {
                  setShowHeroEditor(false);
                  toast.success('Imagen actualizada. Recuerda guardar los cambios.');
                }}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Card */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-16 relative z-10">
        <div 
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8 cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => setEditingWelcome(true)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl lg:text-3xl font-heading text-gray-800 mb-4">
                {data.welcome_title || 'Bienvenida al Espacio de Formación'}
              </h1>
              <p className="text-gray-600 text-sm line-clamp-3">
                {data.welcome_text?.substring(0, 200)}...
              </p>
            </div>
            <button className="p-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <Edit2 className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">Haz clic para editar la bienvenida</p>
        </div>
      </div>

      {/* Modules Gallery */}
      <div className="max-w-4xl mx-auto px-6 py-4">
        {/* Preliminary spheres — Límites only */}
        {isLimitesTpl && (
          <AdminPreliminarySpheres
            courseSlug={courseSlug}
            variant="preliminary"
            title={data?.preliminary_blocks_section_title || 'Esferas introductorias'}
            description="Aparecen como círculos antes del programa formativo. Solo se muestran a las alumnas inscritas."
            onTitleChange={(t) => setData(d => ({ ...d, preliminary_blocks_section_title: t }))}
          />
        )}

        <div className="mb-4">
          <h2 className="text-lg font-heading text-gray-800">Programa formativo:</h2>
          <p className="text-xs text-gray-400">Haz clic en un módulo para editar su contenido</p>
        </div>

        {/* Modules grid - 3 columns sortable */}
        <SortableList
          layout="grid"
          items={[...data.blocks].sort((a, b) => (a.order || 0) - (b.order || 0))}
          onReorder={reorderBlocks}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          renderItem={(block, _idx, { dragHandleProps, isDragging }) => {
            const moduleDisplay = getModuleDisplay(block);
            return (
              <div
                className={`group cursor-pointer ${isDragging ? 'shadow-2xl ring-2 ring-[#c9a227]/30 rounded-lg' : ''}`}
                onClick={() => setSelectedModule(block)}
              >
                {/* Module card - supports image or color background */}
                <div 
                  className="aspect-[4/3] relative flex flex-col justify-center items-center p-3 rounded-t-lg transition-transform group-hover:-translate-y-1 overflow-hidden"
                  style={{ backgroundColor: block.image_url ? (block.hide_overlay ? 'transparent' : '#000') : moduleDisplay.color }}
                >
                  {/* Background image if exists */}
                  {block.image_url && (
                    <img 
                      src={block.image_url}
                      alt={block.title}
                      className={`absolute inset-0 w-full h-full object-cover ${block.hide_overlay ? '' : 'opacity-75'}`}
                      style={{ objectPosition: block.image_position || 'center center' }}
                    />
                  )}
                  
                  {/* Overlay for better text readability on images - only if not hidden */}
                  {block.image_url && !block.hide_overlay && (
                    <div className="absolute inset-0 bg-black/20" />
                  )}

                  {/* Drag handle (top-center, on hover) */}
                  <button
                    type="button"
                    {...dragHandleProps}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-1.5 left-1/2 -translate-x-1/2 p-1 bg-white/90 backdrop-blur-sm rounded shadow text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none z-20"
                    aria-label="Arrastrar para reordenar"
                    data-testid={`drag-block-${block.id}`}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.2"/><circle cx="11" cy="3" r="1.2"/><circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/><circle cx="5" cy="13" r="1.2"/><circle cx="11" cy="13" r="1.2"/></svg>
                  </button>

                  {/* Edit button on hover */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingBlock(block);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    data-testid={`edit-block-${block.id}`}
                  >
                    <Edit2 className="w-3 h-3 text-gray-600" />
                  </button>
                  
                  {/* Lock status */}
                  <div className="absolute top-2 left-2 z-10">
                    {block.is_locked ? (
                      <Lock className="w-3.5 h-3.5" style={{ color: block.image_url ? '#fff' : moduleDisplay.textColor, opacity: 0.6 }} />
                    ) : (
                      <Unlock className="w-3.5 h-3.5" style={{ color: block.image_url ? '#fff' : moduleDisplay.textColor, opacity: 0.6 }} />
                    )}
                  </div>
                  
                  {/* Text overlay - only show if not hidden */}
                  {!block.hide_overlay && (
                    <>
                      {/* Number */}
                      <span 
                        className="text-4xl font-bold leading-none relative z-10"
                        style={{ color: block.image_url ? '#fff' : moduleDisplay.textColor }}
                      >
                        {moduleDisplay.displayNumber}
                      </span>
                      
                      {/* Phase word */}
                      {moduleDisplay.phaseWord && (
                        <span 
                          className="text-sm font-medium mt-1 relative z-10"
                          style={{ color: block.image_url ? '#fff' : moduleDisplay.textColor }}
                        >
                          {moduleDisplay.phaseWord}
                        </span>
                      )}
                      
                      {/* Module label */}
                      <span 
                        className="text-[10px] uppercase tracking-wide mt-0.5 opacity-80 relative z-10"
                        style={{ color: block.image_url ? '#fff' : moduleDisplay.textColor }}
                      >
                        {moduleDisplay.displayNumber === 'i' || moduleDisplay.displayNumber === 'G' 
                          ? block.title 
                          : `MÓDULO ${moduleDisplay.displayNumber}`}
                      </span>
                    </>
                  )}
                </div>
                
                {/* Subtitle - slightly larger */}
                <div className="bg-white p-3 border border-gray-100 border-t-0 rounded-b-lg group-hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm font-medium line-clamp-1">
                        {block.subtitle || block.title}
                      </span>
                    </div>
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBlock(block.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {block.sub_blocks?.length || 0} páginas
                  </p>
                </div>
              </div>
            );
          }}
        />

        {/* Add Module Button (outside sortable list) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <button
            onClick={addBlock}
            className="aspect-[4/3] relative flex flex-col justify-center items-center p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 transition-all cursor-pointer"
          >
            <Plus className="w-8 h-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">Añadir Módulo</span>
          </button>
        </div>

        {/* Practice spheres — Límites only */}
        {isLimitesTpl && (
          <div className="mt-12">
            <AdminPreliminarySpheres
              courseSlug={courseSlug}
              variant="practice"
              title={data?.practice_blocks_section_title || 'Esferas prácticas'}
              description="Aparecen como círculos debajo del programa formativo. Solo se muestran a las alumnas inscritas."
              onTitleChange={(t) => setData(d => ({ ...d, practice_blocks_section_title: t }))}
            />
          </div>
        )}
      </div>

      {/* Edit Welcome Modal */}
      {editingWelcome && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Editar Bienvenida</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingWelcome(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Imagen de cabecera</label>
                <FileUpload
                  value={data.hero_image}
                  onChange={(url) => setData({ ...data, hero_image: url })}
                  accept="image/*"
                  placeholder="Sube una imagen de cabecera"
                  previewClassName="h-32"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Título de bienvenida</label>
                <Input
                  value={data.welcome_title}
                  onChange={(e) => setData({ ...data, welcome_title: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Texto de bienvenida</label>
                <p className="text-xs text-gray-400 mb-2">
                  Usa **texto** para <strong>negrita</strong>, *texto* para <em>cursiva</em>
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Editar:</p>
                    <Textarea
                      value={data.welcome_text}
                      onChange={(e) => setData({ ...data, welcome_text: e.target.value })}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="Escribe el texto de bienvenida..."
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
                    <div className="border rounded-md p-3 h-[280px] overflow-y-auto bg-white text-sm">
                      <MarkdownContent content={data.welcome_text} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setEditingWelcome(false)}>
                Cancelar
              </Button>
              <Button className="flex-1 btn-sage" onClick={() => { saveAll(); setEditingWelcome(false); }}>
                <Check className="w-4 h-4 mr-1" />
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Block Modal */}
      {editingBlock && (
        <EditBlockModal
          block={editingBlock}
          courseSlug={courseSlug}
          limitesTpl={isLimitesTpl}
          onSave={(updates) => updateBlock(editingBlock.id, updates)}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
};

// Edit Block Modal Component
const EditBlockModal = ({ block, courseSlug, limitesTpl, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    ...block,
    module_1_title: block.module_1_title || 'Módulo 1',
    module_2_title: block.module_2_title || 'Módulo 2',
  });
  const [useImage, setUseImage] = useState(!!block.image_url);
  const isLimites = !!limitesTpl;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg">Editar Módulo</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Subtítulo</label>
            <Input
              value={formData.subtitle || ''}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            />
          </div>

          {/* Límites-only: two inline module sections inside this sphere */}
          {isLimites && (
            <div className="rounded-xl border border-[#D4A84B]/30 bg-[#FDFBF5] p-4 space-y-3">
              <p className="text-xs uppercase tracking-wider text-[#8a7428]">
                Dentro de esta esfera (solo Límites)
              </p>
              <p className="text-xs text-gray-500 -mt-1">
                Cada esfera muestra al alumno dos secciones apiladas. Edita libremente sus títulos.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Título de la 1ª sección</label>
                <Input
                  value={formData.module_1_title}
                  onChange={(e) => setFormData({ ...formData, module_1_title: e.target.value })}
                  placeholder="Ej: Módulo 1 — El umbral"
                  data-testid="edit-block-module-1-title"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Título de la 2ª sección</label>
                <Input
                  value={formData.module_2_title}
                  onChange={(e) => setFormData({ ...formData, module_2_title: e.target.value })}
                  placeholder="Ej: Módulo 2 — El campo relacional"
                  data-testid="edit-block-module-2-title"
                />
              </div>
              <div className="pt-2 border-t border-[#D4A84B]/20">
                <label className="block text-xs font-medium text-gray-700 mb-1">Texto del nodo en el mandala</label>
                <Input
                  value={formData.mandala_label ?? ''}
                  onChange={(e) => setFormData({ ...formData, mandala_label: e.target.value })}
                  placeholder="Vacío = título del módulo"
                  data-testid="edit-block-mandala-label"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Línea de fechas (móvil)</label>
                <Input
                  value={formData.mandala_detail ?? ''}
                  onChange={(e) => setFormData({ ...formData, mandala_detail: e.target.value })}
                  placeholder="Ej: Semanas 1–2"
                  data-testid="edit-block-mandala-detail"
                />
              </div>
            </div>
          )}
          
          {/* Toggle between color and image */}
          <div>
            <label className="block text-sm font-medium mb-2">Fondo del módulo</label>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => { setUseImage(false); setFormData({ ...formData, image_url: '' }); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm border transition-colors ${
                  !useImage ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                Color
              </button>
              <button
                onClick={() => setUseImage(true)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm border transition-colors ${
                  useImage ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                Imagen
              </button>
            </div>
            
            {!useImage ? (
              <div className="flex gap-2 flex-wrap">
                {BLOCK_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color && !formData.image_url ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <FileUpload
                  value={formData.image_url || ''}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  accept="image/*"
                  placeholder="Sube una imagen de fondo"
                  previewClassName="h-24"
                />
                {formData.image_url && (
                  <ImagePositioner
                    imageUrl={formData.image_url}
                    position={formData.image_position || '50% 50%'}
                    onChange={(pos) => setFormData({ ...formData, image_position: pos })}
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_locked"
              checked={formData.is_locked}
              onChange={(e) => setFormData({ ...formData, is_locked: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_locked" className="text-sm">
              Bloqueado (requiere desbloqueo manual)
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hide_overlay"
              checked={formData.hide_overlay}
              onChange={(e) => setFormData({ ...formData, hide_overlay: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="hide_overlay" className="text-sm">
              Ocultar texto sobre la imagen (usar imagen con texto propio)
            </label>
          </div>
        </div>
        
        <div className="flex gap-2 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="flex-1 btn-sage" onClick={() => onSave(formData)}>
            <Check className="w-4 h-4 mr-1" />
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Edit Sub-block Modal Component
const EditSubBlockModal = ({ subBlock, courseSlug, limitesTpl, block, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    ...subBlock,
    module_index: Number(subBlock.module_index) === 2 ? 2 : 1,
  });
  const isLimites = !!limitesTpl;
  const m1Title = (block && block.module_1_title) || 'Módulo 1';
  const m2Title = (block && block.module_2_title) || 'Módulo 2';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg">Editar Página</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>

          {/* Límites-only: choose which inline section (Módulo 1 / Módulo 2) this page belongs to */}
          {isLimites && (
            <div className="rounded-xl border border-[#D4A84B]/30 bg-[#FDFBF5] p-3">
              <p className="text-xs font-medium text-[#8a7428] mb-2">Sección dentro de la esfera</p>
              <div className="grid grid-cols-2 gap-2">
                {[1, 2].map((idx) => {
                  const label = idx === 1 ? m1Title : m2Title;
                  const active = formData.module_index === idx;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({ ...formData, module_index: idx })}
                      className={`text-left px-3 py-2 rounded-lg text-xs border transition-colors ${
                        active
                          ? 'bg-[#D4A84B]/20 border-[#D4A84B] text-[#5e4f1a] font-medium'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-[#D4A84B]/40'
                      }`}
                      data-testid={`subblock-section-${idx}`}
                    >
                      <span className="block text-[10px] uppercase tracking-wider opacity-70">{idx === 1 ? '1ª sección' : '2ª sección'}</span>
                      <span className="block truncate">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Imagen de portada</label>
            <FileUpload
              value={formData.image_url || ''}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
              accept="image/*"
              placeholder="Sube una imagen de portada"
              previewClassName="h-32"
            />
            {formData.image_url && (
              <div className="mt-3">
                <ImagePositioner
                  imageUrl={formData.image_url}
                  position={formData.image_position || '50% 50%'}
                  onChange={(pos) => setFormData({ ...formData, image_position: pos })}
                  aspectRatio="16/9"
                />
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de contenido</label>
            <select
              value={formData.content_type || 'text'}
              onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
              className="w-full border rounded-md p-2 text-sm"
            >
              {CONTENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              {formData.content_type === 'video' ? 'Video' :
               formData.content_type === 'pdf' ? 'PDF' :
               formData.content_type === 'link' ? 'URL del enlace' : 'Contenido'}
            </label>
            {formData.content_type === 'text' ? (
              <>
                <p className="text-xs text-gray-400 mb-2">
                  Usa **texto** para <strong>negrita</strong>, *texto* para <em>cursiva</em>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Textarea
                    value={formData.content || ''}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={8}
                    className="font-mono text-sm"
                    placeholder="Escribe el contenido..."
                  />
                  <div className="border rounded-md p-3 overflow-y-auto bg-white text-sm max-h-[200px]">
                    <MarkdownContent content={formData.content} />
                  </div>
                </div>
              </>
            ) : formData.content_type === 'video' ? (
              <FileUpload
                value={formData.content || ''}
                onChange={(url) => setFormData({ ...formData, content: url })}
                accept="video/*"
                placeholder="Sube un video"
                previewClassName="h-40"
              />
            ) : formData.content_type === 'pdf' ? (
              <div className="space-y-1.5">
                <Input
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="https://drive.google.com/file/d/1AbC.../view?usp=sharing"
                  data-testid="pdf-url-input"
                />
                <div className="text-[11px] text-slate-500 leading-relaxed space-y-1">
                  <p>
                    <strong className="text-slate-700">Recomendado — Google Drive:</strong>
                    {' '}Sube el PDF a tu Drive → botón derecho → <em>Compartir</em> → cambia el acceso a <strong>«Cualquier persona con el enlace»</strong> → copia el enlace y pégalo aquí.
                  </p>
                  <p className="text-slate-400">
                    Detectaremos automáticamente que es un enlace de Drive y lo mostraremos embebido dentro de la lección (con visor y botón de descarga). También aceptamos URLs de otros CDN (Cloudinary, R2, etc.).
                  </p>
                  <p className="text-amber-600">
                    Evita subidas locales al servidor: no son persistentes en producción y se pierden con cada despliegue.
                  </p>
                </div>
                {formData.content && formData.content.startsWith('http') && (
                  <a
                    href={formData.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 mt-1"
                    data-testid="pdf-url-preview"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Verificar que abre correctamente
                  </a>
                )}
              </div>
            ) : (
              <Input
                value={formData.content || ''}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="https://..."
              />
            )}
          </div>
        </div>
        
        <div className="flex gap-2 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="flex-1 btn-sage" onClick={() => onSave(formData)}>
            <Check className="w-4 h-4 mr-1" />
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminStudentArea;
